import services from '../services'

const POLL_INTERVAL_MS = 60000 // 60s — alinhado com /participants

/**
 * Auto-seleciona o bucket de timeline baseado na duração do período.
 * Evita o usuário ter que escolher manualmente — pode sempre sobrescrever.
 */
const autoBucket = (from, to) => {
  const hours = (to.getTime() - from.getTime()) / 3600000
  if (hours <= 2) return '1min'
  if (hours <= 12) return '5min'
  if (hours <= 48) return '15min'
  if (hours <= 24 * 7) return '1h'
  return '1d'
}

/**
 * Resolve `from`/`to` a partir do `period` selecionado. Para 'custom' e
 * 'single_day' mantém os valores que vieram (já vêm prontos do picker).
 *
 * Para `event_day_N`, lê N do sufixo e usa o `event.days[N-1]` do filterOptions
 * (já no fuso da app). `event_all` cobre o evento inteiro do init_date 00:00
 * até o end_date 23:59.
 */
const resolveDateRange = (period, current, event) => {
  const now = new Date()
  if (period && period.startsWith('event_day_') && event && Array.isArray(event.days)) {
    const idx = parseInt(period.slice('event_day_'.length), 10)
    const day = event.days.find(d => d.index === idx)
    if (day) {
      const start = new Date(day.date + 'T00:00:00')
      const end = new Date(day.date + 'T23:59:59')
      return { from: start, to: end }
    }
  }
  if (period === 'event_all' && event && event.init_date && event.end_date) {
    return {
      from: new Date(event.init_date + 'T00:00:00'),
      to: new Date(event.end_date + 'T23:59:59')
    }
  }
  switch (period) {
    case 'last_hour':
      return { from: new Date(now.getTime() - 3600000), to: now }
    case 'today': {
      const start = new Date(now)
      start.setHours(0, 0, 0, 0)
      return { from: start, to: now }
    }
    case '7d':
      return { from: new Date(now.getTime() - 7 * 24 * 3600000), to: now }
    case 'custom':
    case 'single_day':
      return { from: current.from, to: current.to }
    default:
      return { from: current.from, to: current.to }
  }
}

export default {
  async loadFilterOptions ({ state, commit }) {
    try {
      const { data } = await services.filterOptions()
      commit('SET_FILTER_OPTIONS', data)

      // Toda vez que entra no dash com evento configurado, força o filtro
      // pra ser event-aware:
      //  - Hoje cai em um dia do evento → seleciona aquele dia (D{N})
      //  - Hoje está fora (pré ou pós) → "Evento todo"
      // Sobrescreve o que estava persistido em localStorage. Usuário pode
      // mudar manualmente depois — só não deixa "preso" num filtro velho
      // (ex.: D2 do mês passado quando já está em outro evento).
      const ev = data && data.event
      if (!ev || ev.status === 'unconfigured') return

      const todayDay = (ev.days || []).find(d => d.date === ev.today)
      if (todayDay) {
        const start = new Date(todayDay.date + 'T00:00:00')
        const end = new Date(todayDay.date + 'T23:59:59')
        commit('SET_FILTERS', { period: `event_day_${todayDay.index}`, from: start, to: end })
        commit('SET_BUCKET', autoBucket(start, end))
      } else if (ev.init_date && ev.end_date) {
        const start = new Date(ev.init_date + 'T00:00:00')
        const end = new Date(ev.end_date + 'T23:59:59')
        commit('SET_FILTERS', { period: 'event_all', from: start, to: end })
        commit('SET_BUCKET', autoBucket(start, end))
      }
    } catch (e) { /* dropdown só fica vazio */ }
  },

  /**
   * Aplica um conjunto de filtros parciais. Recalcula data se period mudou,
   * recalcula bucket, dispara fetch.
   *
   * `partial` pode incluir `from`/`to` direto (vindo do DateRangePicker)
   * pros modos 'custom' e 'single_day'. Aí esses valores prevalecem sobre
   * o resolveDateRange.
   */
  async setFilters ({ state, commit, dispatch }, partial) {
    let merged = { ...state.filters, ...partial }
    if (partial.period && !(partial.from && partial.to)) {
      // Quick selects + event_day_N + event_all — calcula com base em "agora"
      // ou nas datas do evento
      const event = state.filterOptions && state.filterOptions.event
      const range = resolveDateRange(partial.period, merged, event)
      merged = { ...merged, ...range }
    }
    commit('SET_FILTERS', merged)
    if (partial.period) {
      commit('SET_BUCKET', autoBucket(merged.from, merged.to))
    }
    await dispatch('refresh')
  },

  setBucket ({ commit, dispatch }, bucket) {
    commit('SET_BUCKET', bucket)
    return dispatch('refresh')
  },

  /**
   * Recarrega só o TagFlowGrid quando o usuário muda o window dos chips.
   * Não toca nos outros componentes — refresh local mais leve.
   */
  async setTagWindow ({ state, commit }, minutes) {
    commit('SET_BYTAG_WINDOW', minutes)
    try {
      const resp = await services.byTag(state.filters, minutes)
      commit('SET_BY_TAG', resp.data)
    } catch (e) { /* silenciosamente mantém valor anterior */ }
  },

  async refresh ({ state, commit, dispatch }) {
    if (state.isLoading) return
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      // Endpoints "core" sempre buscados — uso paralelo do cache 30s.
      const [overview, timeline, insights, byTag, heatmap, operators, anomalies, funnel, flow, topLocations] = await Promise.all([
        services.overview(state.filters),
        services.timeline(state.filters, state.bucket),
        services.insights(state.filters),
        services.byTag(state.filters, state.byTagWindowMinutes),
        services.heatmap(state.filters),
        services.operators(state.filters),
        services.anomalies(state.filters),
        services.funnel(state.filters),
        services.flow(state.filters),
        services.topLocations(state.filters)
      ])
      commit('SET_OVERVIEW', overview.data)
      commit('SET_TIMELINE', timeline.data)
      commit('SET_INSIGHTS', insights.data)
      commit('SET_BY_TAG', byTag.data)
      commit('SET_HEATMAP', heatmap.data)
      commit('SET_OPERATORS', operators.data)
      commit('SET_ANOMALIES', anomalies.data)
      commit('SET_FUNNEL', funnel.data)
      commit('SET_FLOW', flow.data)
      commit('SET_TOP_LOCATIONS', topLocations.data)
      commit('SET_LAST_UPDATE', new Date())

      // Análise por campo só roda quando há um campo selecionado. Não
      // queremos puxar este endpoint pesado se nem está visível na tela.
      if (state.fieldAnalysis.primary) {
        dispatch('reloadFieldAnalysis')
      }
    } catch (e) {
      commit('SET_ERROR', e && e.message ? e.message : 'Erro ao carregar')
    } finally {
      commit('SET_LOADING', false)
      commit('SET_FIRST_LOAD_DONE')
    }
  },

  /**
   * Recarrega só o card de análise por campo.
   * `primary` e `secondary` são objects { source, id } ou null.
   */
  async reloadFieldAnalysis ({ state, commit }) {
    const { primary, secondary } = state.fieldAnalysis
    if (!primary) {
      commit('SET_FIELD_DATA', null)
      return
    }
    commit('SET_FIELD_LOADING', true)
    try {
      const resp = await services.byField(state.filters, primary, secondary || null)
      commit('SET_FIELD_DATA', resp.data)
    } catch (e) {
      commit('SET_FIELD_DATA', null)
    } finally {
      commit('SET_FIELD_LOADING', false)
    }
  },

  setPrimaryField ({ commit, dispatch }, fieldRef) {
    commit('SET_FIELD_PRIMARY', fieldRef)
    return dispatch('reloadFieldAnalysis')
  },

  setSecondaryField ({ commit, dispatch }, fieldRef) {
    commit('SET_FIELD_SECONDARY', fieldRef)
    return dispatch('reloadFieldAnalysis')
  },

  /**
   * Abre o drawer com detalhes analíticos de uma tag específica.
   * Carrega dados via endpoint dedicado e mostra mesmo enquanto carrega
   * (UI mostra skeletons no drawer).
   */
  async openTagDetail ({ state, commit }, tagId) {
    commit('SET_TAG_DETAIL_LOADING', true)
    commit('SET_TAG_DETAIL', { tag: { id: tagId, name: '...' }, kpis: {}, timeline: [] })
    try {
      const resp = await services.tagDetail(state.filters, tagId, state.tagDetailBucket)
      commit('SET_TAG_DETAIL', resp.data)
    } catch (e) {
      commit('SET_TAG_DETAIL', null)
    } finally {
      commit('SET_TAG_DETAIL_LOADING', false)
    }
  },

  closeTagDetail ({ commit }) {
    commit('CLOSE_TAG_DETAIL')
  },

  async setTagDetailBucket ({ state, commit }, bucket) {
    commit('SET_TAG_DETAIL_BUCKET', bucket)
    if (!state.tagDetail || !state.tagDetail.tag) return
    const tagId = state.tagDetail.tag.id
    commit('SET_TAG_DETAIL_LOADING', true)
    try {
      const resp = await services.tagDetail(state.filters, tagId, bucket)
      commit('SET_TAG_DETAIL', resp.data)
    } catch (e) { /* mantém valor anterior */ }
    commit('SET_TAG_DETAIL_LOADING', false)
  },

  /**
   * Drawer analítico de UM operador. Mesmo padrão do openTagDetail mas
   * escopa por user_operator_id.
   */
  async openOperatorDetail ({ state, commit }, operatorId) {
    commit('SET_OPERATOR_DETAIL_LOADING', true)
    commit('SET_OPERATOR_DETAIL', { operator: { id: operatorId, name: '...' }, kpis: {} })
    try {
      const resp = await services.operatorDetail(state.filters, operatorId)
      commit('SET_OPERATOR_DETAIL', resp.data)
    } catch (e) {
      commit('SET_OPERATOR_DETAIL', null)
    } finally {
      commit('SET_OPERATOR_DETAIL_LOADING', false)
    }
  },

  closeOperatorDetail ({ commit }) {
    commit('CLOSE_OPERATOR_DETAIL')
  },

  /**
   * Análise por campo DENTRO do drawer de operador — escopa por operador.
   * Reusa byField com filtros { operator_id } injetado. Suporta cruzamento
   * de 2 campos.
   */
  async setOperatorDetailField ({ state, commit, dispatch }, fieldRef) {
    commit('SET_OPERATOR_DETAIL_FIELD', fieldRef)
    if (!fieldRef) commit('SET_OPERATOR_DETAIL_FIELD_SECONDARY', null)
    return dispatch('reloadOperatorDetailField')
  },

  async setOperatorDetailFieldSecondary ({ commit, dispatch }, fieldRef) {
    commit('SET_OPERATOR_DETAIL_FIELD_SECONDARY', fieldRef)
    return dispatch('reloadOperatorDetailField')
  },

  async reloadOperatorDetailField ({ state, commit }) {
    const primary = state.operatorDetailField
    const secondary = state.operatorDetailFieldSecondary
    if (!primary || !state.operatorDetail || !state.operatorDetail.operator) {
      commit('SET_OPERATOR_DETAIL_FIELD_DATA', null)
      return
    }
    const operatorId = state.operatorDetail.operator.id
    const scopedFilters = { ...state.filters, operatorId }
    commit('SET_OPERATOR_DETAIL_FIELD_LOADING', true)
    try {
      const resp = await services.byField(scopedFilters, primary, secondary || null)
      commit('SET_OPERATOR_DETAIL_FIELD_DATA', resp.data)
    } catch (e) {
      commit('SET_OPERATOR_DETAIL_FIELD_DATA', null)
    } finally {
      commit('SET_OPERATOR_DETAIL_FIELD_LOADING', false)
    }
  },

  /**
   * Análise por campo DENTRO do drawer — escopa pra um único local.
   * Reusa o endpoint /by-field, mas força tagIds = [tag do drawer].
   * Suporta cruzamento de 2 campos. Estado vive em tagDetailField* e
   * é resetado quando o drawer fecha (mutation CLOSE_TAG_DETAIL).
   */
  async setTagDetailField ({ state, commit, dispatch }, fieldRef) {
    commit('SET_TAG_DETAIL_FIELD', fieldRef)
    // Limpar primary derruba secondary (não faz sentido cruzar sem primary)
    if (!fieldRef) commit('SET_TAG_DETAIL_FIELD_SECONDARY', null)
    return dispatch('reloadTagDetailField')
  },

  async setTagDetailFieldSecondary ({ commit, dispatch }, fieldRef) {
    commit('SET_TAG_DETAIL_FIELD_SECONDARY', fieldRef)
    return dispatch('reloadTagDetailField')
  },

  async reloadTagDetailField ({ state, commit }) {
    const primary = state.tagDetailField
    const secondary = state.tagDetailFieldSecondary
    if (!primary || !state.tagDetail || !state.tagDetail.tag) {
      commit('SET_TAG_DETAIL_FIELD_DATA', null)
      return
    }
    const tagId = state.tagDetail.tag.id
    const scopedFilters = { ...state.filters, tagIds: [tagId] }
    commit('SET_TAG_DETAIL_FIELD_LOADING', true)
    try {
      const resp = await services.byField(scopedFilters, primary, secondary || null)
      commit('SET_TAG_DETAIL_FIELD_DATA', resp.data)
    } catch (e) {
      commit('SET_TAG_DETAIL_FIELD_DATA', null)
    } finally {
      commit('SET_TAG_DETAIL_FIELD_LOADING', false)
    }
  },

  /**
   * Inicia polling. Chama refresh imediatamente e agenda intervalo.
   * Idempotente — se já estiver rodando, primeiro para.
   */
  async startPolling ({ state, commit, dispatch }) {
    await dispatch('stopPolling')
    await dispatch('refresh')
    const handle = setInterval(() => dispatch('refresh'), POLL_INTERVAL_MS)
    commit('SET_POLLING_HANDLE', handle)
  },

  stopPolling ({ state, commit }) {
    if (state.pollingHandle) {
      clearInterval(state.pollingHandle)
      commit('SET_POLLING_HANDLE', null)
    }
  },

  /**
   * Recarrega filtros do localStorage e re-aplica resolveDateRange pra
   * datas (que não persistem — sempre recalculadas em relação a "agora").
   */
  hydrate ({ state, commit }) {
    commit('HYDRATE_FROM_LOCALSTORAGE')
    // Note: `event` ainda não está hidratado aqui (loadFilterOptions roda
    // logo depois). Para event_day_N persistido, o picker reconcilia o
    // estado quando o filterOptions chegar.
    const event = state.filterOptions && state.filterOptions.event
    const range = resolveDateRange(state.filters.period, state.filters, event)
    commit('SET_FILTERS', range)
  }
}
